import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import { changedRowClass } from '@/features/ai-changeset/utils/changeset-view';
import { ChannelLines } from '@/features/menu/components/ProductDraftLines';
import { productFieldTarget, type ProductView } from '@/features/menu/utils/menu-draft';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { Product } from '@restaurant/api-contracts';
import { BlockFooter, BlockTitle, List, ListItem } from 'framework7-react';

type ProductDetail = Product;

// ─── Pricing section ─────────────────────────────────────────────────────────

export default function PricingSection({
  product,
  menuId,
  view,
  store,
}: {
  product: ProductDetail;
  menuId: string;
  view: ProductView;
  store: NavigationStore;
}) {
  const t = useT();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { basePrice, channels } = view;
  const changed = !!basePrice || channels.length > 0;
  const target = productFieldTarget(product.id, 'price');

  // The base price is the dine-in/default price. Tapping the row opens the
  // Pricing page where per-channel (pickup / delivery) prices can be set.
  return (
    <>
      <BlockTitle>{t.menu.pricing}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem
          title={t.menu.basePrice}
          after={basePrice ? undefined : formatMoney(product.price, currency)}
          link={`/menu/${menuId}/product/${product.id}/pricing`}
          className={changedRowClass(changed ? 'single' : null) || undefined}
          data-quick-edit-target={changed ? target : undefined}
          data-testid="product-price-row"
        >
          {changed && (
            <ChangePulseMarker
              slot="root-start"
              store={store}
              id={target}
              testId="product-price-pulse"
            />
          )}
          {basePrice && (
            <ProposedValue
              slot="after"
              before={formatMoney(basePrice.before, currency)}
              after={formatMoney(basePrice.after, currency)}
            />
          )}
          {channels.length > 0 && (
            <div slot="footer" className="quick-edit-detail-lines">
              <ChannelLines
                channels={channels}
                currency={currency}
                testId="product-price-channel"
              />
            </div>
          )}
        </ListItem>
      </List>
      <BlockFooter>{t.menu.basePriceSectionFooter}</BlockFooter>
    </>
  );
}
